import Icon from '@components/Icon/Icon';
import { CloseIcon } from '@components/Icons/CloseIcon';
import { DownArrowIcon } from '@components/Icons/DownArrowIcon';
import { LoadingSpinner } from '@components/Icons/LoadingSpinner';
import { PauseIcon } from '@components/Icons/PauseIcon';
import { PlayIcon } from '@components/Icons/PlayIcon';
import { UpArrowIcon } from '@components/Icons/UpArrowIcon';
import { Tooltip } from '@components/Tooltip';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { extraQueryParams } from '@utils/constants';
import { generateShuffleOrder } from '@utils/shuffleUtils';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { placeHolderData } from '../../../../config';
import {
  getPlaylistTracks,
  getPlaylists,
  removeTrackFromPlaylist,
  reorderPlaylistTracks,
} from '../../../../services/api';
import { setPlaylistsByCategory } from '../../../../store/reducers/commonStore';
import { useAppDispatch } from '../../../../store/store';
import { showToast } from '../../../../utils/toast';
import { AddToPlaylistModal } from '../AddToPlaylistModal/AddToPlaylistModal';
import { ConfirmRemoveModal } from './ConfirmRemoveModal';

interface PlaylistQueueProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  onTrackSelect: (index: number) => void;
  isLoading?: boolean;
  onMinimize?: () => void;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
}

export const PlaylistQueue = memo(
  ({
    isPlaying,
    onPlayPause,
    onTrackSelect,
    isLoading = false,
    onMinimize,
    audioRef,
  }: PlaylistQueueProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
      activePlaylist,
      //musicPlaying,
      shuffleEnabled,
      setShuffleEnabled,
      shuffleOrder,
      setShuffleOrder,
      repeatMode,
      setRepeatMode,
      setActivePlaylist,
    } = useMusicState();
    const { playlistOpen, setPlaylistOpen } = useCommonState();
    const { isAuthenticated, signinRedirect } = useAuth();
    const isStationMode = useStationMode();
    const [isExpanded, setIsExpanded] = useState(false);
    const [startY, setStartY] = useState(0);

    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Animation states for shuffle rearranging
    const [isRearranging, setIsRearranging] = useState(false);
    const [animationKey, setAnimationKey] = useState(0);
    const [trackPositions, setTrackPositions] = useState<{
      [key: string]: { oldIndex: number; newIndex: number };
    }>({});

    // Track loading state for individual tracks
    const [loadingTrackIndex, setLoadingTrackIndex] = useState<number | null>(
      null,
    );

    // Confirmation modal state
    const [confirmRemoveTrackId, setConfirmRemoveTrackId] = useState<
      string | null
    >(null);
    const [confirmRemoveTrackName, setConfirmRemoveTrackName] = useState('');
    const [confirmRemoveItemType, setConfirmRemoveItemType] = useState<
      'track' | 'creation' | undefined
    >(undefined);
    const [isRemoving, setIsRemoving] = useState(false);

    // Add to My Playlists modal state
    const [addToPlaylistTrackId, setAddToPlaylistTrackId] = useState<
      string | null
    >(null);
    const [addToPlaylistTrackName, setAddToPlaylistTrackName] = useState('');
    const [addToPlaylistCreatorName, setAddToPlaylistCreatorName] =
      useState('');
    const [addToPlaylistCategory, setAddToPlaylistCategory] = useState<
      'music' | 'serenity' | 'book' | 'creation'
    >('music');
    const [addToPlaylistItemType, setAddToPlaylistItemType] = useState<
      'track' | 'creation' | undefined
    >(undefined);
    const [addToPlaylistItemId, setAddToPlaylistItemId] = useState<
      string | undefined
    >(undefined);
    const [addToPlaylistCreationOrgId, setAddToPlaylistCreationOrgId] =
      useState<string | undefined>(undefined);

    // Drag and drop state
    const [draggedTrackId, setDraggedTrackId] = useState<string | null>(null);
    const [dragOverTrackId, setDragOverTrackId] = useState<string | null>(null);
    const [isReordering, setIsReordering] = useState(false);

    // Hover state for individual track items
    const [hoveredTrackId, setHoveredTrackId] = useState<string | null>(null);

    // Pagination state
    const [paginationData, setPaginationData] = useState<any>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const handleTrackClick = useCallback(
      (index: number) => {
        // Set loading state for the clicked track
        setLoadingTrackIndex(index);
        onTrackSelect(index);
      },
      [onTrackSelect],
    );

    const toggleShuffle = useCallback(() => {
      // Disable shuffle for book category
      if (activePlaylist?.album.category === 'book') return;

      const newShuffleEnabled = !shuffleEnabled;

      if (!activePlaylist) return;

      // Calculate position changes for SoundCloud-style animation
      const allTracks = activePlaylist.album.tracks;
      const currentDisplayTracks =
        shuffleEnabled && shuffleOrder.length > 0
          ? [
              {
                ...allTracks[activePlaylist.currentIndex],
                originalIndex: activePlaylist.currentIndex,
              },
              ...shuffleOrder
                .filter((index) => index !== activePlaylist.currentIndex)
                .map((index) => ({
                  ...allTracks[index],
                  originalIndex: index,
                })),
            ]
          : allTracks.map((track, index) => ({
              ...track,
              originalIndex: index,
            }));

      const positions: {
        [key: string]: { oldIndex: number; newIndex: number };
      } = {};

      // Start rearranging animation
      setIsRearranging(true);

      // Generate new shuffle order immediately
      if (newShuffleEnabled) {
        const newShuffleOrder = generateShuffleOrder(
          activePlaylist.album.tracks.length,
          activePlaylist.currentIndex,
          activePlaylist.album.tracks, // Pass tracks to filter out unavailable ones
        );
        setShuffleOrder(newShuffleOrder);

        // Calculate position changes for remaining tracks only
        const currentIndex = activePlaylist.currentIndex;
        const remainingTracksStartIndex = currentIndex + 1;

        currentDisplayTracks.forEach((track, oldIndex) => {
          if (track.originalIndex <= currentIndex) {
            // Already played tracks and current track NEVER move
            positions[track.id] = { oldIndex, newIndex: oldIndex };
          } else {
            // Only remaining tracks can move, and only within their area
            const newShuffleIndex = newShuffleOrder.findIndex(
              (idx) => idx === track.originalIndex,
            );
            const newPositionInRemainingArea =
              newShuffleIndex - remainingTracksStartIndex;

            // Calculate new position relative to the remaining tracks area
            const newIndex =
              remainingTracksStartIndex + newPositionInRemainingArea;

            // Ensure the new position is never above the current track
            const safeNewIndex = Math.max(newIndex, remainingTracksStartIndex);

            positions[track.id] = { oldIndex, newIndex: safeNewIndex };
          }
        });
      } else {
        // Turning shuffle off - return to normal order
        setShuffleOrder([]);
        currentDisplayTracks.forEach((track, oldIndex) => {
          positions[track.id] = { oldIndex, newIndex: track.originalIndex };
        });
      }

      setTrackPositions(positions);

      // Update shuffle state
      setShuffleEnabled(newShuffleEnabled);

      // Update animation key to trigger re-render
      setAnimationKey((prev) => prev + 1);

      // End rearranging animation after tracks have moved
      setTimeout(() => {
        setIsRearranging(false);
        setTrackPositions({});
      }, 600); // SoundCloud-style duration
    }, [shuffleEnabled, activePlaylist, setShuffleOrder]);

    const toggleRepeat = useCallback(() => {
      switch (repeatMode) {
        case 'off':
          setRepeatMode('all');
          break;
        case 'all':
          setRepeatMode('one');
          break;
        case 'one':
          setRepeatMode('off');
          break;
        default:
          setRepeatMode('off');
      }
    }, [repeatMode]);

    const handleRemoveTrack = useCallback(
      (trackId: string, trackName: string, itemType?: 'track' | 'creation') => {
        setConfirmRemoveTrackId(trackId);
        setConfirmRemoveTrackName(trackName);
        setConfirmRemoveItemType(itemType);
      },
      [],
    );

    const handleAddToPlaylist = useCallback(
      (
        trackId: string,
        trackName: string,
        creatorName?: string,
        itemType?: 'track' | 'creation',
        itemId?: string,
        creationOrgId?: string,
      ) => {
        // Check if user is authenticated
        if (!isAuthenticated) {
          console.log('User not authenticated, redirecting to login...');
          const fullPath = window.location.pathname + window.location.search;
          signinRedirect({
            extraQueryParams,
            state: { returnUrl: fullPath },
          }).catch((error) => {
            console.error('Error during sign-in redirect:', error);
          });
          return;
        }
        setAddToPlaylistTrackId(trackId);
        setAddToPlaylistTrackName(trackName);
        setAddToPlaylistCreatorName(creatorName || '');
        setAddToPlaylistItemType(itemType);
        setAddToPlaylistItemId(itemId);
        setAddToPlaylistCreationOrgId(creationOrgId);
        // Get category from current album/playlist
        // For creations (my-creations or itemType='creation'), use 'music' category
        // so creations are added to music playlists, not a separate creation category
        if (
          activePlaylist?.album.category === 'my-creations' ||
          activePlaylist?.album.category === 'creation' ||
          itemType === 'creation'
        ) {
          setAddToPlaylistCategory('music');
        } else if (activePlaylist?.album.category) {
          setAddToPlaylistCategory(
            activePlaylist.album.category as
              | 'music'
              | 'serenity'
              | 'book'
              | 'creation',
          );
        }
      },
      [activePlaylist?.album.category, isAuthenticated, signinRedirect],
    );

    const handleAddToPlaylistClick = useCallback(
      (e: React.MouseEvent, track: any) => {
        e.stopPropagation();
        const creatorName =
          activePlaylist?.album?.type === 'playlist'
            ? track.contributors?.[0]?.givenName
            : activePlaylist?.album?.contributors?.[0]?.givenName;
        // Determine if this is a creation based on album category or track itemType
        const isCreation =
          activePlaylist?.album.category === 'my-creations' ||
          activePlaylist?.album.category === 'creation' ||
          track.itemType === 'creation';
        // Get creationOrgId from album (for my-creations) or from track (for creation playlists)
        const creationOrgId = isCreation
          ? (activePlaylist?.album as any)?.orgId ||
            track.creationOrgId ||
            track.orgId
          : undefined;
        handleAddToPlaylist(
          track.id,
          track.name,
          creatorName,
          isCreation ? 'creation' : undefined,
          isCreation ? track.id : undefined,
          creationOrgId,
        );
      },
      [activePlaylist, handleAddToPlaylist],
    );

    const handleConfirmRemove = useCallback(
      async (trackId: string, itemType?: 'track' | 'creation') => {
        // Get the current activePlaylist from the store
        const currentPlaylist = activePlaylist;

        if (!currentPlaylist || currentPlaylist.album.type !== 'playlist') {
          console.error('Not a playlist or no active playlist');
          return;
        }

        console.log(
          '🗑️ Removing track:',
          trackId,
          'from playlist:',
          currentPlaylist.album.id,
          'itemType:',
          itemType,
        );
        setIsRemoving(true);
        try {
          await removeTrackFromPlaylist(
            currentPlaylist.album.id,
            trackId,
            itemType,
          );
          showToast.success(
            itemType === 'creation'
              ? t('player.creationRemoved')
              : t('player.trackRemoved'),
          );

          // Refresh the playlist data
          const updatedPlaylist = await getPlaylistTracks(
            currentPlaylist.album.id,
          );

          console.log('🗑️ Updated playlist:', updatedPlaylist);

          if (
            updatedPlaylist &&
            updatedPlaylist.tracks &&
            updatedPlaylist.tracks.length > 0
          ) {
            // Determine which track to play next
            const currentTrackIndex = currentPlaylist.currentIndex;
            let newIndex = currentTrackIndex;

            // If the deleted track was the currently playing track
            if (
              currentPlaylist.album.tracks[currentTrackIndex]?.id === trackId
            ) {
              // Try to play the next track
              if (currentTrackIndex < updatedPlaylist.tracks.length) {
                newIndex = currentTrackIndex;
              }
              // If no next track, try to play the previous track
              else if (currentTrackIndex > 0) {
                newIndex = currentTrackIndex - 1;
              }
              // Otherwise, play the first track
              else {
                newIndex = 0;
              }
            } else {
              // If deleted track was before current track, adjust index
              if (currentTrackIndex > 0) {
                newIndex = Math.min(
                  currentTrackIndex - 1,
                  updatedPlaylist.tracks.length - 1,
                );
              } else {
                newIndex = 0;
              }
            }

            // Stop audio before updating playlist (don't clear src, let useAudioLoader handle it)
            if (audioRef?.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }

            // Update the active playlist with new tracks and new index
            // Force audio reload by creating a new object reference
            const updatedActivePlaylist = {
              ...currentPlaylist,
              album: {
                ...currentPlaylist.album,
                tracks: updatedPlaylist.tracks,
              },
              currentIndex: newIndex,
            };

            setActivePlaylist(updatedActivePlaylist);
            // Force autoplay of the new track by triggering play
            onPlayPause?.();
          } else {
            console.log('🗑️ No tracks left in playlist');
            setActivePlaylist(null);
          }
          setConfirmRemoveTrackId(null);
          // Notify other components (e.g., PlaylistsPage) that playlist was updated
          window.dispatchEvent(new CustomEvent('playlistUpdated'));
          // Notify AddToPlaylistModal to refresh playlists in Redux
          window.dispatchEvent(new CustomEvent('playlistTrackRemoved'));
        } catch (error) {
          console.error('Error removing track from playlist:', error);
          showToast.error(t('player.removeFailed'));
        } finally {
          setIsRemoving(false);
        }
      },
      [activePlaylist, setActivePlaylist],
    );

    // Drag and drop handlers
    const handleDragStart = useCallback((trackId: string) => {
      setDraggedTrackId(trackId);
    }, []);

    const handleDragOver = useCallback(
      (e: React.DragEvent, trackId: string) => {
        e.preventDefault();
        setDragOverTrackId(trackId);
      },
      [],
    );

    const handleDragLeave = useCallback(() => {
      setDragOverTrackId(null);
    }, []);

    const handleDrop = useCallback(
      async (e: React.DragEvent, targetTrackId: string) => {
        e.preventDefault();
        setDragOverTrackId(null);

        if (
          !draggedTrackId ||
          draggedTrackId === targetTrackId ||
          !activePlaylist
        ) {
          return;
        }

        const tracks = activePlaylist.album.tracks;
        const draggedIndex = tracks.findIndex((t) => t.id === draggedTrackId);
        const targetIndex = tracks.findIndex((t) => t.id === targetTrackId);

        if (draggedIndex === -1 || targetIndex === -1) {
          return;
        }

        // Create new tracks array with reordered items
        const newTracks = [...tracks];
        const [draggedTrack] = newTracks.splice(draggedIndex, 1);
        newTracks.splice(targetIndex, 0, draggedTrack);

        // Find the currently playing track ID
        const currentlyPlayingTrackId = tracks[activePlaylist.currentIndex]?.id;

        // Calculate the new index of the currently playing track in the reordered array
        const newCurrentIndex = currentlyPlayingTrackId
          ? newTracks.findIndex((t) => t.id === currentlyPlayingTrackId)
          : activePlaylist.currentIndex;

        // Update local state immediately for UI feedback
        setActivePlaylist({
          ...activePlaylist,
          album: {
            ...activePlaylist.album,
            tracks: newTracks,
          },
          currentIndex:
            newCurrentIndex >= 0
              ? newCurrentIndex
              : activePlaylist.currentIndex,
        });

        // Call API to persist the reorder
        setIsReordering(true);
        try {
          // Find the previous and next track IDs for the dragged track
          const draggedTrackNewIndex = newTracks.findIndex(
            (t) => t.id === draggedTrackId,
          );
          const prevTrackId =
            draggedTrackNewIndex > 0
              ? newTracks[draggedTrackNewIndex - 1].id
              : null;
          const nextTrackId =
            draggedTrackNewIndex < newTracks.length - 1
              ? newTracks[draggedTrackNewIndex + 1].id
              : null;

          console.log('🔄 Reordering playlist:', {
            playlistId: activePlaylist.album.id,
            trackId: draggedTrackId,
            prevTrackId,
            nextTrackId,
            itemType: draggedTrack.itemType,
          });

          await reorderPlaylistTracks(
            activePlaylist.album.id,
            draggedTrackId,
            prevTrackId,
            nextTrackId,
            draggedTrack.itemType,
          );
          showToast.success(t('player.playlistReordered'));

          // Refetch all playlists for this category from API
          const category = activePlaylist.album.category as
            | 'music'
            | 'serenity'
            | 'book';
          try {
            console.log(
              `📡 Fetching ${category} playlists from API after reorder...`,
            );
            const response = await getPlaylists(category, 50, 0);

            if (response?.items) {
              console.log(
                `✅ Fetched ${category} playlists (${response.items.length} items)`,
              );
              // Update Redux store with all playlists for this category
              dispatch(
                setPlaylistsByCategory({
                  category,
                  playlists: response.items,
                }),
              );
              console.log(`📦 Redux store updated with ${category} playlists`);
            }
          } catch (reduxError) {
            console.error(
              'Error refetching playlists after reorder:',
              reduxError,
            );
          }
        } catch (error) {
          console.error('Error reordering playlist:', error);
          showToast.error(t('player.reorderFailed'));
          // Revert to original order on error
          setActivePlaylist({
            ...activePlaylist,
            album: {
              ...activePlaylist.album,
              tracks,
            },
          });
        } finally {
          setIsReordering(false);
          setDraggedTrackId(null);
        }
      },
      [draggedTrackId, activePlaylist, setActivePlaylist, dispatch],
    );

    // Get the display order of tracks (shuffled or normal)
    const getDisplayTracks = useCallback(() => {
      if (!activePlaylist) return [];

      const allTracks = activePlaylist.album.tracks;
      if (shuffleEnabled && shuffleOrder.length > 0) {
        // Show tracks in the exact shuffle order - don't recalculate based on current track
        // This keeps the display stable when playing next/previous tracks
        return shuffleOrder.map((index) => ({
          ...allTracks[index],
          originalIndex: index,
        }));
      } else {
        // Return tracks in normal order
        return allTracks.map((track, index) => ({
          ...track,
          originalIndex: index,
        }));
      }
    }, [activePlaylist, shuffleEnabled, shuffleOrder]);

    const handleExpand = useCallback(() => {
      setIsExpanded(true);
    }, []);

    const handleMinimize = useCallback(() => {
      setIsExpanded(false);
    }, []);

    // Simplified touch handlers only for the drag handle
    const handleDragHandleTouch = useCallback((e: React.TouchEvent) => {
      e.stopPropagation();
      setStartY(e.touches[0].clientY);
      setIsDragging(true);
    }, []);

    const handleDragHandleMove = useCallback(
      (e: React.TouchEvent) => {
        if (!isDragging) return;

        e.stopPropagation();
        const y = e.touches[0].clientY;
        const diff = y - startY;

        // If dragging down and not expanded, allow collapse
        if (diff > 50 && !isExpanded) {
          setPlaylistOpen(false);
        }
        // If dragging up, expand
        else if (diff < -50 && !isExpanded) {
          setIsExpanded(true);
        }
      },
      [isDragging, startY, isExpanded, setPlaylistOpen],
    );

    const handleDragHandleEnd = useCallback(() => {
      setIsDragging(false);
    }, []);

    // Reset expanded state when modal closes
    useEffect(() => {
      if (!playlistOpen) {
        setIsExpanded(false);
      }
    }, [playlistOpen]);

    // Clear loading state when track changes or loading completes
    useEffect(() => {
      if (!isLoading && loadingTrackIndex !== null) {
        setLoadingTrackIndex(null);
      }
    }, [isLoading, loadingTrackIndex]);

    // Clear loading state when active track changes
    useEffect(() => {
      if (
        activePlaylist &&
        loadingTrackIndex !== null &&
        loadingTrackIndex === activePlaylist.currentIndex
      ) {
        setLoadingTrackIndex(null);
      }
    }, [activePlaylist?.currentIndex, loadingTrackIndex]);

    // Handle load more tracks
    const handleLoadMore = useCallback(async () => {
      if (
        !activePlaylist ||
        !paginationData ||
        !paginationData.hasNext ||
        isLoadingMore
      ) {
        return;
      }

      setIsLoadingMore(true);
      try {
        const nextOffset = paginationData.offset + paginationData.limit;
        const response = await getPlaylistTracks(
          activePlaylist.album.id,
          paginationData.limit,
          nextOffset,
        );

        if (response && response.tracks && response.tracks.items) {
          // Merge new tracks with existing tracks
          const newTracks = [
            ...activePlaylist.album.tracks,
            ...response.tracks.items,
          ];

          // Update active playlist with merged tracks
          setActivePlaylist({
            ...activePlaylist,
            album: {
              ...activePlaylist.album,
              tracks: newTracks,
            },
          });

          // Update pagination data
          if (response.tracks.pagination) {
            setPaginationData(response.tracks.pagination);
          }

          showToast.success(t('player.loadedMoreTracks'));
        }
      } catch (error) {
        console.error('Error loading more tracks:', error);
        showToast.error(t('player.loadMoreFailed'));
      } finally {
        setIsLoadingMore(false);
      }
    }, [activePlaylist, paginationData, isLoadingMore, setActivePlaylist]);

    // Capture pagination data when playlist changes
    useEffect(() => {
      if (
        activePlaylist &&
        activePlaylist.album.type === 'playlist' &&
        activePlaylist.album.tracks
      ) {
        // Check if we have pagination info in the album data
        // This would be set when the playlist is first loaded
        if ((activePlaylist.album as any).pagination) {
          setPaginationData((activePlaylist.album as any).pagination);
        }
      }
    }, [activePlaylist?.album.id]);

    if (!playlistOpen || !activePlaylist) return null;

    const currentTrack =
      activePlaylist.album.tracks[activePlaylist.currentIndex];

    // Get current contributor for playlists
    const currentContributor =
      activePlaylist?.album?.type === 'playlist'
        ? currentTrack?.contributors?.[0]
        : activePlaylist?.album?.contributors?.[0];

    const displayTracks = getDisplayTracks();
    // For creations, use thumbnailUrl from track root; for regular tracks, use album.thumbnailUrl
    const albumArtUrl =
      currentTrack?.thumbnailUrl || // Creation's thumbnail
      currentTrack?.album?.thumbnailUrl ||
      currentContributor?.thumbnailUrl ||
      placeHolderData.albumCover;
    return (
      <>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
            playlistOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } ${isStationMode ? '' : 'sm:hidden'}`}
          onClick={() => setPlaylistOpen(false)}
        />

        {/* Desktop Sidebar */}
        <div
          data-playlist-queue
          className={`fixed right-0 top-0 h-full w-96 bg-black/95 backdrop-blur-sm z-[999] flex-col transform transition-transform duration-300 ${
            isStationMode
              ? 'hidden'
              : `hidden group sm:flex ${
                  playlistOpen ? 'translate-x-0' : 'translate-x-full'
                }`
          }`}>
          {/* Desktop Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-white text-xl font-semibold text-left">
              {t('player.queue')}
            </h2>
            <button
              onClick={onMinimize}
              className="p-2 hover:bg-white/10 rounded-full pointer transition-colors text-white">
              <CloseIcon width={24} height={24} />
            </button>
          </div>

          {/* Desktop Content */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{ maxHeight: 'calc(100% - 120px)' }}>
            {/* Now Playing Section */}
            <div className="p-6 border-b border-white/10">
              <h3 className="text-white/60 text-base font-medium mb-3 text-left">
                {t('player.nowPlaying')}
              </h3>

              {/* Current Track */}
              <div className="flex items-center gap-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                {/* Contributor Thumbnail or Album Art */}
                {albumArtUrl ? (
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                    <img
                      src={albumArtUrl}
                      alt={
                        activePlaylist.album.albumName ||
                        currentContributor?.givenName ||
                        'Album Art'
                      }
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : activePlaylist.album?.albumArtUrl ? (
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                    <img
                      src={activePlaylist.album.albumArtUrl}
                      alt={activePlaylist.album.albumName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : null}

                <div className="flex-1 min-w-0 text-left">
                  <h4 className="text-orange-400 font-medium truncate text-lg">
                    {currentTrack.name}
                  </h4>
                  <p className="text-white/60 text-sm truncate">
                    {currentContributor?.givenName ||
                      activePlaylist.album?.albumName}
                  </p>
                </div>

                <button
                  onClick={onPlayPause}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors text-white flex-shrink-0">
                  {isLoading ? (
                    <LoadingSpinner width={20} height={20} />
                  ) : isPlaying ? (
                    <PauseIcon />
                  ) : (
                    <PlayIcon width={20} height={20} />
                  )}
                </button>
              </div>
            </div>

            {/* Full Playlist */}
            <div className="p-6">
              <h3 className="text-white/60 text-base font-medium mb-3 text-left">
                {t('player.queueFrom', { name: activePlaylist.album?.albumName })}
              </h3>

              <div className="space-y-1">
                {displayTracks.map((track, displayIndex) => {
                  const originalIndex = track.originalIndex;
                  const isCurrentTrack =
                    originalIndex === activePlaylist.currentIndex;
                  const isDeleted = track.isAvailable === false;

                  // Calculate isPreviousTrack based on shuffle order position, not original index
                  const isPreviousTrack =
                    shuffleEnabled && shuffleOrder.length > 0
                      ? (() => {
                          const currentTrackShufflePos = shuffleOrder.indexOf(
                            activePlaylist.currentIndex,
                          );
                          const thisTrackShufflePos =
                            shuffleOrder.indexOf(originalIndex);
                          return thisTrackShufflePos < currentTrackShufflePos;
                        })()
                      : originalIndex < activePlaylist.currentIndex;

                  // Calculate SoundCloud-style animation transform
                  const getSoundCloudTransform = () => {
                    if (!isRearranging || !trackPositions[track.id]) {
                      return 'translateY(0px)';
                    }

                    const { oldIndex, newIndex } = trackPositions[track.id];
                    const currentTrackIndex = activePlaylist.currentIndex;

                    // NEVER animate tracks at or before current track position
                    if (originalIndex <= currentTrackIndex) {
                      return 'translateY(0px)';
                    }

                    // Don't animate tracks that don't move
                    if (oldIndex === newIndex) {
                      return 'translateY(0px)';
                    }

                    const positionDiff = newIndex - oldIndex;

                    // Each track item is approximately 72px tall (including gap)
                    const trackHeight = 72;
                    const translateY = positionDiff * trackHeight;

                    // Extra safety: prevent any upward movement that would go above current track
                    const maxUpwardMovement =
                      (oldIndex - currentTrackIndex - 1) * trackHeight;
                    const safeTranslateY = Math.max(
                      -translateY,
                      -maxUpwardMovement,
                    );

                    return `translateY(${safeTranslateY}px)`;
                  };

                  return (
                    <div
                      key={`${track?.url}-${originalIndex}-${animationKey}`}
                      draggable={
                        activePlaylist?.album.type === 'playlist' &&
                        !isReordering &&
                        !isDeleted
                      }
                      onDragStart={() =>
                        !isDeleted && handleDragStart(track.id)
                      }
                      onDragOver={(e) =>
                        !isDeleted && handleDragOver(e, track.id)
                      }
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => !isDeleted && handleDrop(e, track.id)}
                      onMouseEnter={() => setHoveredTrackId(track.id)}
                      onMouseLeave={() => setHoveredTrackId(null)}
                      className={`flex items-center gap-4 p-4 rounded-lg group transition-all duration-700 ease-out ${
                        isDeleted
                          ? 'opacity-40 cursor-not-allowed'
                          : isCurrentTrack
                            ? 'bg-orange-500/10 border border-orange-500/20 cursor-pointer'
                            : isPreviousTrack
                              ? 'hover:bg-white/5 opacity-60 cursor-pointer'
                              : 'hover:bg-white/5 cursor-pointer'
                      } ${isRearranging ? 'z-10 shadow-lg' : 'z-0'} ${
                        dragOverTrackId === track.id
                          ? 'border-t-2 border-orange-500'
                          : ''
                      } ${draggedTrackId === track.id ? 'opacity-50' : ''}`}
                      style={{
                        transform: getSoundCloudTransform(),
                        transitionDelay: `${displayIndex * 30}ms`,
                        transitionProperty: 'transform, box-shadow, z-index',
                      }}
                      onClick={() =>
                        !isDeleted && handleTrackClick(originalIndex)
                      }>
                      <div className="w-5 text-left">
                        {isCurrentTrack ? (
                          <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
                        ) : (
                          <span
                            className={`text-lg ${
                              isPreviousTrack
                                ? 'text-white/40'
                                : 'text-white/60'
                            }`}>
                            {originalIndex + 1}
                          </span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 text-left">
                        <h4
                          className={`font-medium truncate text-lg ${
                            isDeleted
                              ? 'text-white/40 line-through'
                              : isCurrentTrack
                                ? 'text-orange-400'
                                : isPreviousTrack
                                  ? 'text-white/60'
                                  : 'text-white'
                          }`}>
                          {track.name}
                        </h4>
                        {isDeleted && (
                          <p className="text-xs text-red-400 mt-1">
                            {t('player.deletedTrack')}
                          </p>
                        )}
                      </div>

                      {/* Action Buttons (Move for playlists, Add/Remove based on type) */}
                      <div
                        className={`flex items-center gap-1 transition-opacity ${
                          hoveredTrackId === track.id
                            ? 'opacity-100'
                            : 'opacity-0'
                        }`}>
                        {/* Move Icon (only for playlists) */}
                        {activePlaylist?.album.type === 'playlist' && (
                          <>
                            <Tooltip
                              content={t('player.dragToReorder')}
                              anchor={`#move-icon-${track.id}`}
                              placement="top"
                            />
                            <div
                              id={`move-icon-${track.id}`}
                              className="p-2 rounded-full text-white/60 flex-shrink-0 cursor-grab active:cursor-grabbing hover:text-white/80 transition-colors">
                              <Icon
                                name="move"
                                size={20}
                                color="currentColor"
                              />
                            </div>
                          </>
                        )}

                        {/* Add to My Playlists Button (for non-playlists, including my-creations) */}
                        {activePlaylist?.album.type !== 'playlist' &&
                          activePlaylist?.album.category !== 'generated' && (
                            <>
                              <Tooltip
                                content={t('player.addToPlaylists')}
                                anchor={`#add-playlist-btn-${track.id}`}
                                placement="top"
                              />
                              <button
                                id={`add-playlist-btn-${track.id}`}
                                onClick={(e) =>
                                  handleAddToPlaylistClick(e, track)
                                }
                                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white/80 flex-shrink-0"
                                type="button">
                                <Icon
                                  name="playlist-add"
                                  size={20}
                                  color="currentColor"
                                />
                              </button>
                            </>
                          )}

                        {/* Remove from Playlist Button (only for playlists) */}
                        {activePlaylist?.album.type === 'playlist' && (
                          <>
                            <Tooltip
                              content={t('player.removeFromPlaylist')}
                              anchor={`#remove-playlist-btn-${track.id}`}
                              placement="top"
                            />
                            <button
                              id={`remove-playlist-btn-${track.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTrack(
                                  track.id,
                                  track.name,
                                  track.itemType,
                                );
                              }}
                              className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white/80 flex-shrink-0"
                              type="button">
                              <Icon
                                name="playlist-remove"
                                size={20}
                                color="currentColor"
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load More Button - Only show if pagination exists and has next page */}
              {paginationData && paginationData.hasNext && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="px-6 py-2 bg-orange-500/20 hover:bg-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-orange-400 rounded-lg transition-colors font-medium text-sm">
                    {isLoadingMore ? t('common.loading') : t('player.loadMoreTracks')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Bottom Controls */}
          <div className="p-6 border-t border-white/10">
            <div className="flex items-center justify-between px-8">
              <>
                <Tooltip
                  content={
                    activePlaylist?.album.category === 'book'
                      ? t('player.shuffleNotAvailable')
                      : t('player.shuffle')
                  }
                  anchor="#shuffle-button-queue"
                  placement="top"
                />
                <button
                  id="shuffle-button-queue"
                  onClick={toggleShuffle}
                  disabled={activePlaylist?.album.category === 'book'}
                  className={`p-3 rounded-full transition-colors ${
                    activePlaylist?.album.category === 'book'
                      ? 'text-white/30 cursor-not-allowed opacity-90'
                      : shuffleEnabled
                        ? 'bg-orange-500 text-black'
                        : 'hover:bg-white/10 text-white/60'
                  }`}>
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="currentColor">
                    <path d="M14.83 13.41L13.42 14.82L16.55 17.95L14.5 20H20V14.5L17.96 16.54L14.83 13.41M14.5 4L16.54 6.04L4 18.59L5.41 20L17.96 7.46L20 9.5V4M10.59 9.17L5.41 4L4 5.41L9.17 10.58L10.59 9.17Z" />
                  </svg>
                </button>
              </>

              <button
                onClick={toggleRepeat}
                className={`p-3 rounded-full transition-colors ${
                  repeatMode !== 'off'
                    ? 'bg-orange-500 text-black'
                    : 'hover:bg-white/10 text-white/60'
                }`}
                title={
                  repeatMode === 'one'
                    ? t('player.repeatOne')
                    : repeatMode === 'all'
                      ? t('player.repeatAll')
                      : t('player.repeatOff')
                }>
                <Icon
                  name={repeatMode === 'one' ? 'repeat-one' : 'repeat'}
                  size={20}
                  color="currentColor"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Sheet */}
        <div
          ref={containerRef}
          className={`fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-sm rounded-t-2xl transform transition-transform duration-300 flex flex-col ${
            isStationMode ? '' : 'sm:hidden'
          } ${playlistOpen ? 'translate-y-0' : 'translate-y-full'} ${
            isExpanded ? 'h-full z-[100000]' : 'h-[70vh] z-50'
          }`}>
          {/* Mobile Header with drag handle */}
          <div className="flex flex-col flex-shrink-0">
            {/* Drag Handle - Only this area handles touch */}
            <div
              className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
              onTouchStart={handleDragHandleTouch}
              onTouchMove={handleDragHandleMove}
              onTouchEnd={handleDragHandleEnd}>
              <div className="w-10 h-1 bg-white/30 rounded-full" />
            </div>

            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <h2 className="text-white text-lg font-semibold text-left">
                  {t('player.queue')}
                </h2>
                <button
                  onClick={isExpanded ? handleMinimize : handleExpand}
                  className="p-1 hover:bg-white/10 rounded-full transition-colors text-white/60"
                  title={isExpanded ? t('player.minimizeQueue') : t('player.expandQueue')}>
                  {isExpanded ? <DownArrowIcon /> : <UpArrowIcon />}
                </button>
              </div>
              <button
                onClick={() => setPlaylistOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white">
                <CloseIcon width={24} height={24} />
              </button>
            </div>
          </div>

          {/* Mobile Content - Completely separate from touch handlers */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
            }}>
            {/* Now Playing Section */}
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white/60 text-sm font-medium mb-3 text-left">
                {t('player.nowPlaying')}
              </h3>

              {/* Current Track */}
              <div className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                {/* Contributor Thumbnail or Album Art */}
                {currentContributor?.thumbnailUrl ? (
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden">
                    <img
                      src={currentContributor.thumbnailUrl}
                      alt={currentContributor.givenName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : activePlaylist.album?.albumArtUrl ? (
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden">
                    <img
                      src={activePlaylist.album.albumArtUrl}
                      alt={activePlaylist.album.albumName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : null}

                <div className="flex-1 min-w-0 text-left">
                  <h4 className="text-white font-medium truncate">
                    {currentTrack.name}
                  </h4>
                  <p className="text-orange-400 text-sm truncate">
                    {currentContributor?.givenName ||
                      activePlaylist.album?.albumName}
                  </p>
                </div>

                <button
                  onClick={onPlayPause}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors text-white flex-shrink-0">
                  {isLoading ? (
                    <LoadingSpinner width={20} height={20} />
                  ) : isPlaying ? (
                    <PauseIcon />
                  ) : (
                    <PlayIcon width={20} height={20} />
                  )}
                </button>
              </div>
            </div>

            {/* Full Playlist */}
            <div className="p-4 pb-4">
              <h3 className="text-white/60 text-sm font-medium mb-3 text-left">
                {t('player.queueFrom', { name: activePlaylist.album.albumName })}
              </h3>

              <div className="space-y-1">
                {displayTracks.map((track, displayIndex) => {
                  const originalIndex = track.originalIndex;
                  const isCurrentTrack =
                    originalIndex === activePlaylist.currentIndex;

                  // Calculate isPreviousTrack based on shuffle order position, not original index
                  const isPreviousTrack =
                    shuffleEnabled && shuffleOrder.length > 0
                      ? (() => {
                          const currentTrackShufflePos = shuffleOrder.indexOf(
                            activePlaylist.currentIndex,
                          );
                          const thisTrackShufflePos =
                            shuffleOrder.indexOf(originalIndex);
                          return thisTrackShufflePos < currentTrackShufflePos;
                        })()
                      : originalIndex < activePlaylist.currentIndex;

                  // Calculate SoundCloud-style animation transform for mobile
                  const getSoundCloudTransformMobile = () => {
                    if (!isRearranging || !trackPositions[track.id]) {
                      return 'translateY(0px)';
                    }

                    const { oldIndex, newIndex } = trackPositions[track.id];
                    const currentTrackIndex = activePlaylist.currentIndex;

                    // NEVER animate tracks at or before current track position
                    if (originalIndex <= currentTrackIndex) {
                      return 'translateY(0px)';
                    }

                    // Don't animate tracks that don't move
                    if (oldIndex === newIndex) {
                      return 'translateY(0px)';
                    }

                    const positionDiff = newIndex - oldIndex;

                    // Each track item is approximately 60px tall for mobile (including gap)
                    const trackHeight = 60;
                    const translateY = positionDiff * trackHeight;

                    // Extra safety: prevent any upward movement that would go above current track
                    const maxUpwardMovement =
                      (oldIndex - currentTrackIndex - 1) * trackHeight;
                    const safeTranslateY = Math.max(
                      -translateY,
                      -maxUpwardMovement,
                    );

                    return `translateY(${safeTranslateY}px)`;
                  };

                  return (
                    <div
                      key={`${track.url}-${originalIndex}-${animationKey}`}
                      draggable={
                        activePlaylist?.album.type === 'playlist' &&
                        !isReordering
                      }
                      onDragStart={() => handleDragStart(track.id)}
                      onDragOver={(e) => handleDragOver(e, track.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, track.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer group transition-all duration-700 ease-out ${
                        isCurrentTrack
                          ? 'bg-orange-500/10 border border-orange-500/20'
                          : isPreviousTrack
                            ? 'hover:bg-white/5 opacity-60'
                            : 'hover:bg-white/5'
                      } ${isRearranging ? 'z-10 shadow-lg' : 'z-0'} ${
                        dragOverTrackId === track.id
                          ? 'border-t-2 border-orange-500'
                          : ''
                      } ${draggedTrackId === track.id ? 'opacity-50' : ''}`}
                      style={{
                        transform: getSoundCloudTransformMobile(),
                        transitionDelay: `${displayIndex * 30}ms`,
                        transitionProperty: 'transform, box-shadow, z-index',
                      }}
                      onClick={() => handleTrackClick(originalIndex)}>
                      {/* Track Number or Playing Indicator */}
                      <div className="w-5 text-left">
                        {isCurrentTrack ? (
                          <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
                        ) : (
                          <span
                            className={`text-sm ${
                              isPreviousTrack
                                ? 'text-white/40'
                                : 'text-white/60'
                            }`}>
                            {originalIndex + 1}
                          </span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 text-left">
                        <h4
                          className={`font-medium truncate ${
                            isCurrentTrack
                              ? 'text-orange-400'
                              : isPreviousTrack
                                ? 'text-white/60'
                                : 'text-white'
                          }`}>
                          {track.name}
                        </h4>
                      </div>

                      {/* Action Buttons (Move for playlists, Add/Remove based on type) */}
                      <div className="flex items-center gap-1">
                        {/* Move Icon (only for playlists) */}
                        {activePlaylist?.album.type === 'playlist' && (
                          <>
                            <Tooltip
                              content={t('player.dragToReorder')}
                              anchor={`#move-icon-mobile-${track.id}`}
                              placement="top"
                            />
                            <div
                              id={`move-icon-mobile-${track.id}`}
                              className="p-2 rounded-full transition-colors text-white/60 flex-shrink-0 cursor-grab active:cursor-grabbing hover:text-white/80">
                              <Icon
                                name="move"
                                size={18}
                                color="currentColor"
                              />
                            </div>
                          </>
                        )}

                        {/* Add to My Playlists Button (for non-playlists, including my-creations) */}
                        {activePlaylist?.album.type !== 'playlist' &&
                          activePlaylist?.album.category !== 'generated' && (
                            <>
                              <Tooltip
                                content={t('player.addToPlaylists')}
                                anchor={`#add-playlist-btn-mobile-${track.id}`}
                                placement="top"
                              />
                              <button
                                id={`add-playlist-btn-mobile-${track.id}`}
                                onClick={(e) =>
                                  handleAddToPlaylistClick(e, track)
                                }
                                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white/80"
                                type="button">
                                <Icon
                                  name="playlist-add"
                                  size={18}
                                  color="currentColor"
                                />
                              </button>
                            </>
                          )}

                        {/* Remove from Playlist Button (only for playlists) */}
                        {activePlaylist?.album.type === 'playlist' && (
                          <>
                            <Tooltip
                              content={t('player.removeFromPlaylist')}
                              anchor={`#remove-playlist-btn-mobile-${track.id}`}
                              placement="top"
                            />
                            <button
                              id={`remove-playlist-btn-mobile-${track.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTrack(
                                  track.id,
                                  track.name,
                                  track.itemType,
                                );
                              }}
                              className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60 hover:text-white/80"
                              type="button">
                              <Icon
                                name="playlist-remove"
                                size={18}
                                color="currentColor"
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load More Button - Only show if pagination exists and has next page */}
              {paginationData && paginationData.hasNext && (
                <div className="mt-4 flex justify-center pb-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="px-6 py-2 bg-orange-500/20 hover:bg-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-orange-400 rounded-lg transition-colors font-medium text-sm">
                    {isLoadingMore ? t('common.loading') : t('player.loadMoreTracks')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Bottom Controls - Outside scrollable area */}
          <div className="p-4 border-t border-white/10 flex-shrink-0">
            <div className="flex items-center justify-between px-4">
              <button
                onClick={toggleShuffle}
                className={`p-3 rounded-full transition-colors ${
                  shuffleEnabled
                    ? 'bg-orange-500 text-black'
                    : 'hover:bg-white/10 text-white/60'
                }`}
                title={t('player.shuffle')}>
                <Icon name="shuffle" size={20} color="currentColor" />
              </button>

              <button
                onClick={toggleRepeat}
                className={`p-3 rounded-full transition-colors ${
                  repeatMode !== 'off'
                    ? 'bg-orange-500 text-black'
                    : 'hover:bg-white/10 text-white/60'
                }`}
                title={
                  repeatMode === 'one'
                    ? t('player.repeatOne')
                    : repeatMode === 'all'
                      ? t('player.repeatAll')
                      : t('player.repeatOff')
                }>
                <Icon
                  name={repeatMode === 'one' ? 'repeat-one' : 'repeat'}
                  size={20}
                  color="currentColor"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Confirm Remove Modal */}
        <ConfirmRemoveModal
          isOpen={confirmRemoveTrackId !== null}
          trackName={confirmRemoveTrackName}
          onConfirm={() => {
            if (confirmRemoveTrackId) {
              handleConfirmRemove(confirmRemoveTrackId, confirmRemoveItemType);
            }
          }}
          onCancel={() => {
            setConfirmRemoveTrackId(null);
            setConfirmRemoveTrackName('');
            setConfirmRemoveItemType(undefined);
          }}
          isLoading={isRemoving}
        />

        {/* Add to My Playlists Modal */}
        <AddToPlaylistModal
          isOpen={addToPlaylistTrackId !== null}
          onClose={() => {
            setAddToPlaylistTrackId(null);
            setAddToPlaylistTrackName('');
            setAddToPlaylistCreatorName('');
            setAddToPlaylistItemType(undefined);
            setAddToPlaylistItemId(undefined);
            setAddToPlaylistCreationOrgId(undefined);
          }}
          trackId={addToPlaylistTrackId || ''}
          trackName={addToPlaylistTrackName}
          creatorName={addToPlaylistCreatorName}
          category={addToPlaylistCategory}
          itemType={addToPlaylistItemType}
          itemId={addToPlaylistItemId}
          creationOrgId={addToPlaylistCreationOrgId}
        />
      </>
    );
  },
);

PlaylistQueue.displayName = 'PlaylistQueue';
